import { ActionIcon } from '@mantine/core';

import { brand } from '../../branding';
import { useCatalogLabels } from '../labels';
import { useWishlist, type WishlistItemType } from '../wishlist';

interface WishlistButtonProps {
    /** 'product' (id = artNr) or 'document' (id = documentId, falling back to assetId) - see
     * wishlist.ts. Individual documents can be starred independently of their owning product,
     * matching the legacy DOPI application. */
    type: WishlistItemType;
    id: string;
    size?: number;
}

// DOKA brand blue (matches .mantine-Title-root in App.css) - set explicitly rather than relying
// on the shared @uag theme's default primaryColor, so the marked/unmarked states stay
// unmistakably distinct no matter what that theme configures. Also backed by a same-named,
// higher-specificity CSS rule in App.css (.wishlist-button[data-active]) as a defensive fallback -
// this codebase has a documented history (see App.css's Tailwind-Preflight comment) of plain
// Mantine component styling silently losing the cascade.
const ACTIVE_COLOR = brand.primary;

/** Toggle button for the cookie-based Merkliste - used on product tiles (ProductGrid), the
 * article detail page, and document rows (DocumentSearchResults). Stops click propagation itself
 * so it never triggers a parent row/card's "open" onClick. */
export const WishlistButton = ({ type, id, size = 18 }: WishlistButtonProps) => {
    const { L } = useCatalogLabels();
    const { has, toggle } = useWishlist();
    const active = has(type, id);

    return (
        <ActionIcon
            aria-label={active ? L.removeFromWishlist : L.addToWishlist}
            className="wishlist-button"
            color={ACTIVE_COLOR}
            data-active={active || undefined}
            title={active ? L.removeFromWishlist : L.addToWishlist}
            variant={active ? 'filled' : 'default'}
            onClick={(event) => {
                event.stopPropagation();
                toggle(type, id);
            }}
        >
            <span className="material-symbols-outlined" style={{ fontSize: size }}>
                {active ? 'bookmark_added' : 'bookmark_add'}
            </span>
        </ActionIcon>
    );
};
